import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@educa-legal/ui';
import { services, servicePath } from '../data/services';

type NavChild = {
  label: string;
  to: string;
};

type NavItem = {
  label: string;
  to: string;
  children?: NavChild[];
};

const servicesDropdown: NavChild[] = services.map((s) => ({
  label: s.title,
  to: servicePath(s.slug),
}));

const navItems: NavItem[] = [
  { label: 'Início',                 to: '/' },
  { label: 'Serviços',               to: '/#servicos', children: servicesDropdown },
  { label: 'Sobre Nós',              to: '/sobre-nos' },
  { label: 'Blog',                   to: '/blog' },
  { label: 'Contato',                to: '/contato' },
  { label: 'FAQ',                    to: '/faq' },
];

/**
 * Renders a nav target as a real React Router <Link> when it points to a route,
 * or as an <a> when it contains an in-page anchor (so the browser can scroll).
 */
function NavTarget(props: {
  to: string;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  ariaExpanded?: boolean;
  ariaHasPopup?: 'true' | 'false';
  children: React.ReactNode;
}) {
  const { to, className, onClick, ariaExpanded, ariaHasPopup, children } = props;
  const isAnchor = to.includes('#');
  if (isAnchor) {
    return (
      <a
        href={to}
        className={className}
        onClick={onClick}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      onClick={onClick}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
    >
      {children}
    </Link>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const openWithCancel = (key: string) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenDropdown(key);
  };

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenDropdown(null), 120);
  };

  // When the user is already on "/", clicking the logo or "Início" should just
  // scroll to the top instead of triggering a Link with no navigation.
  const goHome = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // On home, header is transparent until scrolled. On internal pages, always solid.
  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;
  const headerBg = isTransparent
    ? 'bg-transparent'
    : 'bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm';

  // Classe dos links: claros sobre Hero escuro, escuros sobre header solid.
  const navLinkClass = isTransparent
    ? 'inline-flex items-center gap-1 text-sm font-medium text-white/90 hover:text-white transition-colors'
    : 'inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-950 transition-colors';

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${headerBg}`}>
      <div className="section-container">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={goHome}
            className="flex items-center group"
            aria-label="Educa Legal — página inicial"
          >
            <img
              src={isTransparent ? '/logo-on-dark.png' : '/logo-on-white.png'}
              alt="Educa Legal"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <ul className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <li
                key={item.to}
                className="relative"
                onMouseEnter={() => item.children && openWithCancel(item.to)}
                onMouseLeave={() => item.children && scheduleClose()}
              >
                {item.children ? (
                  <button
                    type="button"
                    onClick={() =>
                      setOpenDropdown(openDropdown === item.to ? null : item.to)
                    }
                    className={navLinkClass}
                    aria-expanded={openDropdown === item.to}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${openDropdown === item.to ? 'rotate-180' : ''}`}
                    />
                  </button>
                ) : (
                  <NavTarget
                    to={item.to}
                    onClick={item.to === '/' ? goHome : undefined}
                    className={navLinkClass}
                  >
                    {item.label}
                  </NavTarget>
                )}

                {item.children && openDropdown === item.to && (
                  <div
                    className="absolute left-1/2 top-full -translate-x-1/2 pt-3"
                    onMouseEnter={() => openWithCancel(item.to)}
                    onMouseLeave={scheduleClose}
                  >
                    <ul className="w-[320px] rounded-2xl border border-neutral-200 bg-white shadow-xl p-2">
                      {item.children.map((child) => (
                        <li key={child.to}>
                          <Link
                            to={child.to}
                            onClick={() => setOpenDropdown(null)}
                            className="block rounded-xl px-4 py-2 text-sm text-neutral-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* CTA */}
          <div className="hidden md:block md:mr-[-12px] lg:mr-[-24px]">
            <Button asChild size="sm" variant="primary">
              <a href="https://wa.me/5511989082892" target="_blank" rel="noopener noreferrer">
                Fale com a Educa Legal
              </a>
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 rounded-lg text-neutral-700 hover:bg-neutral-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </nav>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-neutral-200 px-6 py-4 space-y-2">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.to}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between py-2 text-sm font-medium text-neutral-700"
                  onClick={() => setMobileServicesOpen((v) => !v)}
                  aria-expanded={mobileServicesOpen}
                >
                  <span>{item.label}</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {mobileServicesOpen && (
                  <ul className="pl-3 pb-2 space-y-1 border-l border-neutral-200">
                    {item.children.map((child) => (
                      <li key={child.to}>
                        <Link
                          to={child.to}
                          onClick={() => {
                            setIsMenuOpen(false);
                            setMobileServicesOpen(false);
                          }}
                          className="block py-2 text-sm text-neutral-600 hover:text-orange-600"
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <NavTarget
                key={item.to}
                to={item.to}
                className="block text-sm font-medium text-neutral-700 hover:text-orange-600 py-2"
                onClick={(e) => {
                  if (item.to === '/') goHome(e);
                  setIsMenuOpen(false);
                }}
              >
                {item.label}
              </NavTarget>
            ),
          )}
          <Button asChild size="sm" variant="primary" className="w-full justify-center mt-2">
            <a
              href="https://wa.me/5511989082892"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
            >
              Fale com a Educa Legal
            </a>
          </Button>
        </div>
      )}
    </header>
  );
}
